import { Controller } from "@hotwired/stimulus"
import { fetchTurboStream } from "../../src/shared/helpers/turbo_stream_fetch"

const SIRET_LENGTH = 14
// Intra-EU VAT number, as Profiles::Practitioner::VAT_NUMBER_FORMAT; 8 characters
// at least after the prefix, so a number being typed is not checked.
const VAT_NUMBER_FORMAT = /^[A-Z]{2}[0-9A-Z]{8,12}$/
const VAT_DELAY = 500

// Billing: once a full SIRET is typed, asks the company register through the
// server. The answer streams into the `status` target; a found company comes
// with what the form can fill (`result` target), applied to blank fields only;
// an individual business comes with one button per kind (`chooseKind`) and its
// start of activity, a guess for the URSSAF affiliation date.
// While the register is asked, the status shows a spinner and `searching`.
// A company based abroad: its VAT number (`vat` target) is checked with VIES
// (`vatUrl`) once typed, and a valid one fills the billing address the same way.
export default class extends Controller {
  static targets = ["siret", "vat", "status", "result", "companyKind", "affiliatedToUrssafOn"]
  static values = { url: String, searching: String, vatUrl: String, vatSearching: String }

  disconnect () {
    clearTimeout(this.vatTimer)
  }

  async lookup () {
    const siret = this.siretTarget.value.replace(/\D/g, "")
    if (siret.length !== SIRET_LENGTH) {
      this.lastSiret = null
      this.statusTarget.replaceChildren()
      return
    }
    if (siret === this.lastSiret) return

    this.lastSiret = siret
    this.showSearching()

    try {
      const target = encodeURIComponent(this.statusTarget.id)
      if (!(await fetchTurboStream(`${this.urlValue}?siret=${siret}&target=${target}`))) this.clearSearching()
    } catch {
      this.clearSearching()
    }
  }

  lookupVat () {
    clearTimeout(this.vatTimer)
    const vatNumber = this.vatTarget.value.toUpperCase().replace(/[^A-Z0-9]/g, "")
    if (!VAT_NUMBER_FORMAT.test(vatNumber)) {
      this.lastVatNumber = null
      this.statusTarget.replaceChildren()
      return
    }
    if (vatNumber === this.lastVatNumber) return

    this.vatTimer = setTimeout(() => this.fetchVat(vatNumber), VAT_DELAY)
  }

  async fetchVat (vatNumber) {
    this.lastVatNumber = vatNumber
    this.showSearching(this.vatSearchingValue)

    try {
      const target = encodeURIComponent(this.statusTarget.id)
      if (!(await fetchTurboStream(`${this.vatUrlValue}?vat_number=${vatNumber}&target=${target}`))) this.clearSearching()
    } catch {
      this.clearSearching()
    }
  }

  showSearching (message = this.searchingValue) {
    const line = document.createElement("p")
    line.className = "practitioner-company-lookup__line text-muted mb-0"
    line.dataset.searching = ""

    const spinner = document.createElement("i")
    spinner.className = "fa fa-spinner fa-spin"
    spinner.setAttribute("aria-hidden", "true")

    const text = document.createElement("span")
    text.textContent = message

    line.append(spinner, text)
    this.statusTarget.replaceChildren(line)
  }

  // Lets the admin retry the same number after a failed request.
  clearSearching () {
    this.lastSiret = null
    this.lastVatNumber = null
    if (this.statusTarget.querySelector("[data-searching]")) this.statusTarget.replaceChildren()
  }

  resultTargetConnected (result) {
    const { companyKind, address, activityStartedOn } = result.dataset

    if (companyKind && this.hasCompanyKindTarget && !this.companyKindTarget.value) {
      this.companyKindTarget.value = companyKind
      this.companyKindTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }
    if (address) this.fillAddress(address)
    if (activityStartedOn) this.fillAffiliatedToUrssafOn(activityStartedOn)
  }

  // An individual business: the admin says which kind in one click, even over a pick.
  chooseKind ({ params: { kind } }) {
    if (!this.hasCompanyKindTarget) return

    this.companyKindTarget.value = kind
    this.companyKindTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  fillAffiliatedToUrssafOn (date) {
    if (!this.hasAffiliatedToUrssafOnTarget || this.affiliatedToUrssafOnTarget.value) return

    const input = this.affiliatedToUrssafOnTarget
    if (input._flatpickr) {
      input._flatpickr.setDate(date, true)
    } else {
      input.value = date
    }
  }

  fillAddress (address) {
    const element = this.element.querySelector("[data-controller~='address-autocomplete']")
    const autocomplete = element && this.application.getControllerForElementAndIdentifier(element, "address-autocomplete")
    if (!autocomplete || autocomplete.inputTarget.value.trim()) return

    autocomplete.fillFromQuery(address)
  }
}
